import { Box, Table, TableBody } from '@mui/material';

import { Shimmer } from './Shimmer';
import { StatsCardsSkeleton } from './StatsCardsSkeleton';
import { TableSkeleton } from './TableSkeleton';

interface ListPageSkeletonProps {
  /** Stat cards above the table; 0 hides the row. */
  stats?: number;
  columns?: number;
  rows?: number;
  /** The search / filter bar that sits above most tables. */
  withFilters?: boolean;
}

/**
 * Whole-page placeholder for a list screen — title, stat cards, filter bar and
 * table. Used as the route-level Suspense fallback so the lazy page chunk loads
 * behind the same shape the page itself shows while fetching, instead of a
 * spinner that then swaps to a skeleton.
 */
export const ListPageSkeleton = ({
  stats = 3,
  columns = 7,
  rows = 8,
  withFilters = true,
}: ListPageSkeletonProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', pt: '10px' }}>
    {/* Title + primary action */}
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: '12px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0 }}>
        <Shimmer height={{ xs: 20, sm: 22, md: 24 }} width={{ xs: '55%', sm: 220 }} />
        <Shimmer height={14} width={{ xs: '80%', sm: 300 }} />
      </Box>
      <Shimmer height={40} width={{ xs: '100%', sm: 150 }} radius="8px" />
    </Box>

    {stats > 0 && <StatsCardsSkeleton count={stats} />}

    <Box
      sx={(theme) => ({
        bgcolor: 'white',
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: '16px',
        boxShadow: '0px 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      })}
    >
      {withFilters && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: '12px',
            px: { xs: '16px', sm: '20px' },
            py: '16px',
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
          })}
        >
          <Shimmer height={38} radius="8px" sx={{ flex: 1 }} />
          <Shimmer height={38} width={{ xs: '100%', md: 160 }} radius="8px" />
        </Box>
      )}

      {/* The table scrolls inside its own container, as the real ones do. */}
      <Box sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 720 }}>
          <TableBody>
            <TableSkeleton columns={columns} rows={rows} />
          </TableBody>
        </Table>
      </Box>

      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: '12px',
          px: { xs: '16px', sm: '24px' },
          py: '16px',
          borderTop: `1px solid ${theme.palette.grey[200]}`,
        })}
      >
        <Shimmer height={14} width={{ xs: '60%', sm: 180 }} />
        <Shimmer height={32} width={{ xs: '100%', sm: 200 }} radius="8px" />
      </Box>
    </Box>
  </Box>
);
